import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, CheckCircle2, ChevronDown, Factory, Package, Plus, Store, Trash2, Truck } from 'lucide-react'
import {
  useBookShipment,
  useCancelShipment,
  useEditShipment,
  useRebookShipment,
} from '@/api/hooks'
import type { components } from '@/api/schema'
import { ChoicePicker } from '@/components/choice-picker'
import { LoadingButton } from '@/components/loading-button'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatApiErrorMessage } from '@/lib/api-error'
import { cn, formatDateOnly } from '@/lib/utils'
import {
  FREIGHT_MODE_LABEL,
  UNBOOKED_SHIPMENT_STATUSES,
  shipmentFreightMode,
  type FreightMode,
  estimatedHandlingUnits,
  groupedUnitSummaries,
  handlingUnitsMissing,
  pickupAddressMissing,
  pickupDefaults,
  shipmentBookingMissing,
  type HandlingUnitInput,
} from './shipment-booking'
import type { PurchaseOrderDetail } from './types'

type Shipment = PurchaseOrderDetail['shipments'][number]
type EditBody = components['schemas']['EditShipmentRequest']

const MODE_CHOICES = [
  { value: 'parcel', label: 'Small parcel' },
  { value: 'ltl', label: 'LTL' },
] as const

const BOOKER_CHOICES = [
  { value: 'us', label: 'We book' },
  { value: 'manufacturer', label: 'Supplier books' },
] as const

/** Save part of a shipment; failures say what to fix. */
function useShipmentSave(po: PurchaseOrderDetail, shipment: Shipment) {
  const edit = useEditShipment(po.uuid, shipment.uuid)
  const save = (body: EditBody, onSuccess?: () => void) =>
    edit.mutate(
      { shipmentId: shipment.id, body },
      {
        onSuccess,
        onError: (error) => toast.error(formatApiErrorMessage('Shipment not saved', error)),
      },
    )
  return { save, pending: edit.isPending }
}

function Pill({ icon, children, pickable = false }: { icon?: ReactNode; children: ReactNode; pickable?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs font-medium [&_svg]:size-3 [&_svg]:text-muted-foreground">
      {icon}
      {children}
      {pickable && <ChevronDown aria-hidden />}
    </span>
  )
}

const MODE_ICON: Record<FreightMode, ReactNode> = {
  parcel: <Package aria-hidden />,
  ltl: <Truck aria-hidden />,
}

/**
 * LTL or small parcel, picked from a pill. Before booking it simply changes how we book. A
 * shipment we already booked is cancelled with the carrier and booked again in the new mode,
 * after a confirmation. Afterwards it records how the freight travels.
 */
export function ShipmentModePicker({ po, shipment }: { po: PurchaseOrderDetail; shipment: Shipment }) {
  const { save, pending } = useShipmentSave(po, shipment)
  const cancel = useCancelShipment(po.uuid, shipment.uuid)
  const rebook = useRebookShipment(po.uuid)
  const [confirming, setConfirming] = useState<FreightMode | null>(null)
  const mode = shipmentFreightMode(shipment, po)
  const label = mode ? FREIGHT_MODE_LABEL[mode] : 'Mode not set'

  const icon = mode ? MODE_ICON[mode] : undefined
  if (shipment.status === 'cancelled') return <Pill icon={icon}>{label}</Pill>

  const rebooksCarrier = shipment.status === 'booked' && shipment.booked_by === 'us'
  const pick = (next: string) => {
    const nextMode = next as FreightMode
    if (rebooksCarrier) {
      setConfirming(nextMode)
      return
    }
    save({ freight_mode: nextMode }, () => toast.success(`Shipment set to ${FREIGHT_MODE_LABEL[nextMode]}`))
  }
  const switchBooking = (nextMode: FreightMode) =>
    cancel.mutate(
      { shipmentId: shipment.id, reason: `Switching to ${FREIGHT_MODE_LABEL[nextMode]}` },
      {
        onSuccess: () =>
          rebook.mutate(
            { shipmentId: shipment.id, body: { freight_mode: nextMode } },
            {
              onSuccess: () => {
                setConfirming(null)
                toast.success(`Booking ${FREIGHT_MODE_LABEL[nextMode]} — the new label or BOL goes to the supplier`)
              },
              onError: (error) => {
                setConfirming(null)
                toast.error(formatApiErrorMessage('Cancelled, but the new booking did not start', error))
              },
            },
          ),
        onError: (error) => toast.error(formatApiErrorMessage('Booking not cancelled', error)),
      },
    )

  return (
    <>
      <ChoicePicker
        title="Ships as"
        label={`Ships as: ${label}. Change`}
        value={mode ?? ''}
        choices={MODE_CHOICES}
        pending={pending || cancel.isPending || rebook.isPending}
        onSelect={pick}
      >
        <Pill icon={icon} pickable>
          {label}
        </Pill>
      </ChoicePicker>
      <Dialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Rebook as {confirming ? FREIGHT_MODE_LABEL[confirming] : ''}?
            </DialogTitle>
            <DialogDescription>
              We cancel the {shipment.carrier_name || 'current'} booking and book{' '}
              {confirming ? FREIGHT_MODE_LABEL[confirming] : ''} instead. The supplier gets the new
              label or BOL.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Keep booking
            </Button>
            <LoadingButton
              loading={cancel.isPending || rebook.isPending}
              loadingText="Rebooking..."
              onClick={() => confirming && switchBooking(confirming)}
            >
              Rebook
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** Who books the freight, picked from a pill while the shipment is not booked yet. */
export function ShipmentBookerPicker({ po, shipment }: { po: PurchaseOrderDetail; shipment: Shipment }) {
  const { save, pending } = useShipmentSave(po, shipment)
  const value = shipment.booked_by === 'manufacturer' ? 'manufacturer' : 'us'
  const label = BOOKER_CHOICES.find((choice) => choice.value === value)?.label ?? 'We book'
  if (!UNBOOKED_SHIPMENT_STATUSES.has(shipment.status)) return null
  return (
    <ChoicePicker
      title="Who books the freight"
      label={`${label}. Change who books`}
      value={value}
      choices={BOOKER_CHOICES}
      pending={pending}
      onSelect={(next) => save({ booked_by: next }, () => toast.success(next === 'us' ? 'We book this shipment' : 'Supplier books this shipment'))}
    >
      <Pill icon={value === 'us' ? <Store aria-hidden /> : <Factory aria-hidden />} pickable>
        {label}
      </Pill>
    </ChoicePicker>
  )
}

function Row({
  label,
  ok,
  children,
}: {
  label: string
  /** null: optional, no check mark either way. */
  ok: boolean | null
  children: ReactNode
}) {
  return (
    // An open editor takes the row's full width; the label moves above it.
    <div className="group/row grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-2 py-2 sm:grid-cols-[1.25rem_7.5rem_minmax(0,1fr)] sm:has-[form]:grid-cols-[1.25rem_minmax(0,1fr)]">
      <span className="flex h-8 items-center self-start" aria-hidden>
        {ok === true && <CheckCircle2 className="size-4 text-emerald-600" />}
        {ok === false && <AlertTriangle className="size-4 text-amber-600" />}
      </span>
      <span className="flex min-h-8 items-center self-start text-xs font-medium text-muted-foreground max-sm:min-h-0 max-sm:pt-2 group-has-[form]/row:min-h-0 group-has-[form]/row:pt-2">
        {label}
        {ok === false && <span className="sr-only"> (needed to book)</span>}
      </span>
      <div className="min-w-0 max-sm:col-start-2 group-has-[form]/row:col-start-2">{children}</div>
    </div>
  )
}

function Suggestion({ children, onClick, pending }: { children: ReactNode; onClick: () => void; pending: boolean }) {
  return (
    <LoadingButton
      type="button"
      variant="outline"
      size="sm"
      className="h-7 text-xs"
      loading={pending}
      loadingText="Saving..."
      onClick={onClick}
    >
      {children}
    </LoadingButton>
  )
}

const ADDRESS_FIELDS = [
  { key: 'ship_from_name', label: 'Company', span: 'sm:col-span-6' },
  { key: 'ship_from_contact_name', label: 'Contact', span: 'sm:col-span-6' },
  { key: 'ship_from_phone', label: 'Phone', span: 'sm:col-span-3', inputMode: 'tel' },
  { key: 'ship_from_email', label: 'Email', span: 'sm:col-span-3' },
  { key: 'ship_from_address1', label: 'Address', span: 'sm:col-span-6' },
  { key: 'ship_from_address2', label: 'Address line 2', span: 'sm:col-span-6' },
  { key: 'ship_from_city', label: 'City', span: 'sm:col-span-3' },
  { key: 'ship_from_state', label: 'State', span: 'sm:col-span-1' },
  { key: 'ship_from_zip', label: 'ZIP', span: 'sm:col-span-2', inputMode: 'numeric' },
] as const

type AddressKey = (typeof ADDRESS_FIELDS)[number]['key']

function addressLine(values: Partial<Record<AddressKey, string | null | undefined>>) {
  const cityLine = [values.ship_from_city, values.ship_from_state].filter(Boolean).join(', ')
  return [
    values.ship_from_name,
    values.ship_from_address1,
    values.ship_from_address2,
    [cityLine, values.ship_from_zip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The pickup address, click to edit in place. Empty fields open filled from what we know. */
function PickupEditor({ po, shipment }: { po: PurchaseOrderDetail; shipment: Shipment }) {
  const { save, pending } = useShipmentSave(po, shipment)
  const defaults = pickupDefaults(po, shipment.id)
  const current = Object.fromEntries(
    ADDRESS_FIELDS.map(({ key }) => [key, (shipment[key] as string | null | undefined) ?? '']),
  ) as Record<AddressKey, string>
  const [draft, setDraft] = useState<Record<AddressKey, string> | null>(null)
  const missing = pickupAddressMissing(shipment)
  const phoneMissing = !shipment.ship_from_phone
  const suggestedPhone = phoneMissing ? defaults.ship_from_phone : ''

  if (draft) {
    const submit = () => {
      const body: Record<string, string | null> = {}
      for (const { key } of ADDRESS_FIELDS) {
        if (draft[key].trim() !== current[key]) body[key] = draft[key].trim() || null
      }
      if (Object.keys(body).length === 0) return setDraft(null)
      save(body as EditBody, () => setDraft(null))
    }
    return (
      <form
        className="-mx-2 space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
        onKeyDown={(event) => event.key === 'Escape' && setDraft(null)}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
          {ADDRESS_FIELDS.map((field) => (
            <div key={field.key} className={cn('col-span-2 space-y-1', field.span)}>
              <Label htmlFor={`${shipment.id}-${field.key}`} className="text-xs text-muted-foreground">
                {field.label}
              </Label>
              <InlineFieldInput
                id={`${shipment.id}-${field.key}`}
                value={draft[field.key]}
                inputMode={'inputMode' in field ? field.inputMode : undefined}
                maxLength={field.key === 'ship_from_state' ? 2 : undefined}
                autoFocus={field.key === 'ship_from_name'}
                onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(null)}>
            Cancel
          </Button>
          <LoadingButton type="submit" size="sm" loading={pending} loadingText="Saving...">
            Save pickup
          </LoadingButton>
        </div>
      </form>
    )
  }

  const open = () =>
    setDraft(
      Object.fromEntries(
        ADDRESS_FIELDS.map(({ key }) => [key, current[key] || defaults[key] || '']),
      ) as Record<AddressKey, string>,
    )
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={open}
        aria-label="Edit pickup address"
        className="-mx-2 block w-[calc(100%+1rem)] rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <span className="block break-words">{addressLine(current) || <span className="text-muted-foreground">Add pickup address</span>}</span>
        {(shipment.ship_from_contact_name || shipment.ship_from_phone) && (
          <span className="block text-xs text-muted-foreground">
            {[shipment.ship_from_contact_name, shipment.ship_from_phone].filter(Boolean).join(' · ')}
          </span>
        )}
        {(missing.length > 0 || phoneMissing) && (
          <span className="block text-xs text-amber-700 dark:text-amber-400">
            Needs {[...missing, ...(phoneMissing ? ['pickup phone'] : [])].join(', ')}
          </span>
        )}
      </button>
      {suggestedPhone && (
        <Suggestion pending={pending} onClick={() => save({ ship_from_phone: suggestedPhone })}>
          Use {suggestedPhone}
        </Suggestion>
      )}
    </div>
  )
}

interface UnitDraft {
  key: string
  unit_type: string
  num_units: string
  length_in: string
  width_in: string
  height_in: string
  weight_lbs: string
}

const UNIT_TYPES = ['Carton', 'Box', 'Pallet', 'Crate', 'Bundle', 'Drum', 'Roll', 'Other']

let unitKey = 0
const nextUnitKey = () => `unit-${++unitKey}`

function toDraft(unit?: Shipment['handling_units'][number]): UnitDraft {
  const weight = (unit?.commodities ?? []).reduce((sum, c) => sum + Number(c.weight_lbs ?? 0), 0)
  const text = (value: unknown) => (value != null && Number(value) > 0 ? String(Number(value)) : '')
  return {
    key: nextUnitKey(),
    unit_type: unit?.unit_type ?? 'Carton',
    num_units: String(unit?.num_units ?? 1),
    length_in: text(unit?.length_in),
    width_in: text(unit?.width_in),
    height_in: text(unit?.height_in),
    weight_lbs: weight > 0 ? String(weight) : '',
  }
}

function fromDraft(draft: UnitDraft, description: string): HandlingUnitInput {
  const num = (value: string) => (Number(value) > 0 ? Number(value) : null)
  return {
    unit_type: draft.unit_type,
    num_units: Math.max(1, Number(draft.num_units) || 1),
    stackable: false,
    length_in: num(draft.length_in),
    width_in: num(draft.width_in),
    height_in: num(draft.height_in),
    commodities: num(draft.weight_lbs)
      ? [{ description, weight_lbs: Number(draft.weight_lbs), pieces: 1 }]
      : [],
  }
}

/** Boxes and pallets, one row each, click to edit in place with add and remove. */
function UnitsEditor({ po, shipment }: { po: PurchaseOrderDetail; shipment: Shipment }) {
  const { save, pending } = useShipmentSave(po, shipment)
  const units = shipment.handling_units ?? []
  const [drafts, setDrafts] = useState<UnitDraft[] | null>(null)
  const estimate = units.length === 0 ? estimatedHandlingUnits(po) : []
  const description =
    units[0]?.commodities?.[0]?.description || `Restaurant equipment PO ${po.po_number}`
  const missing = handlingUnitsMissing(units)

  if (drafts) {
    const update = (key: string, patch: Partial<UnitDraft>) =>
      setDrafts(drafts.map((d) => (d.key === key ? { ...d, ...patch } : d)))
    const cell = (draft: UnitDraft, field: keyof UnitDraft, label: string, className?: string) => (
      <div className={cn('space-y-1', className)}>
        <Label htmlFor={`${draft.key}-${field}`} className="text-[11px] text-muted-foreground">
          {label}
        </Label>
        <InlineFieldInput
          id={`${draft.key}-${field}`}
          inputMode="decimal"
          className="tabular-nums"
          value={draft[field]}
          onChange={(event) => update(draft.key, { [field]: event.target.value })}
        />
      </div>
    )
    return (
      <form
        className="-mx-2 space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          save({ handling_units: drafts.map((d) => fromDraft(d, description)) }, () => setDrafts(null))
        }}
        onKeyDown={(event) => event.key === 'Escape' && setDrafts(null)}
      >
        {drafts.map((draft, index) => (
          <div key={draft.key} className="grid grid-cols-6 items-end gap-2 sm:grid-cols-[8rem_4rem_repeat(4,minmax(0,1fr))_2rem]">
            <div className="col-span-4 space-y-1 sm:col-span-1">
              <Label htmlFor={`${draft.key}-type`} className="text-[11px] text-muted-foreground">
                Type
              </Label>
              <Select value={draft.unit_type} onValueChange={(value) => update(draft.key, { unit_type: value })}>
                <SelectTrigger id={`${draft.key}-type`} autoFocus={index === 0} size="sm" className="w-full bg-background px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {cell(draft, 'num_units', 'Qty', 'col-span-2 sm:col-span-1')}
            {cell(draft, 'length_in', 'L (in)', 'col-span-2 sm:col-span-1')}
            {cell(draft, 'width_in', 'W (in)', 'col-span-2 sm:col-span-1')}
            {cell(draft, 'height_in', 'H (in)', 'col-span-2 sm:col-span-1')}
            {cell(draft, 'weight_lbs', 'Weight (lb)', 'col-span-5 sm:col-span-1')}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="col-span-1 size-8 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${draft.unit_type.toLowerCase()} ${index + 1}`}
              onClick={() => setDrafts(drafts.filter((d) => d.key !== draft.key))}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setDrafts([...drafts, toDraft()])}
          >
            <Plus className="mr-1 size-3.5" />
            Add package
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setDrafts(null)}>
              Cancel
            </Button>
            <LoadingButton type="submit" size="sm" loading={pending} loadingText="Saving...">
              Save
            </LoadingButton>
          </div>
        </div>
      </form>
    )
  }

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        aria-label="Edit packages"
        onClick={() => setDrafts(units.length ? units.map((u) => toDraft(u)) : [toDraft()])}
        className="-mx-2 block w-[calc(100%+1rem)] rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {units.length === 0 ? (
          <span className="text-muted-foreground">Add packages</span>
        ) : (
          groupedUnitSummaries(units).map((line) => (
            <span key={line} className="block tabular-nums">
              {line}
            </span>
          ))
        )}
        {units.length > 0 && missing.length > 0 && (
          <span className="block text-xs text-amber-700 dark:text-amber-400">Needs {missing.join(', ')}</span>
        )}
      </button>
      {estimate.length > 0 && (
        <Suggestion pending={pending} onClick={() => save({ handling_units: estimate })}>
          Use PO estimate: {estimate.length} {estimate.length === 1 ? 'package' : 'packages'}
        </Suggestion>
      )}
    </div>
  )
}

const EXTRAS = [
  { key: 'liftgate_required', label: 'Liftgate delivery' },
  { key: 'residential_delivery', label: 'Residential delivery' },
  { key: 'inside_delivery', label: 'Inside delivery' },
  { key: 'appointment_required', label: 'Appointment' },
  { key: 'notify_before_delivery', label: 'Call before delivery' },
  { key: 'liftgate_pickup', label: 'Liftgate pickup' },
  { key: 'residential_pickup', label: 'Residential pickup' },
  { key: 'inside_pickup', label: 'Inside pickup' },
] as const

/** Delivery and pickup extras: the ones on show, each toggled with one click when editing. */
function ExtrasEditor({ po, shipment }: { po: PurchaseOrderDetail; shipment: Shipment }) {
  const { save, pending } = useShipmentSave(po, shipment)
  const [editing, setEditing] = useState(false)
  const on = EXTRAS.filter(({ key }) => Boolean(shipment[key]))
  if (!editing) {
    return (
      <button
        type="button"
        aria-label="Edit delivery and pickup extras"
        onClick={() => setEditing(true)}
        className="-mx-2 flex min-h-8 w-[calc(100%+1rem)] flex-wrap items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {on.length === 0 ? (
          <span className="text-muted-foreground">None</span>
        ) : (
          on.map(({ key, label }) => <Pill key={key}>{label}</Pill>)
        )}
      </button>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 py-1">
      {EXTRAS.map(({ key, label }) => {
        const active = Boolean(shipment[key])
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            disabled={pending}
            onClick={() => save({ [key]: !active } as EditBody)}
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {label}
          </button>
        )
      })}
      <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => setEditing(false)}>
        Done
      </Button>
    </div>
  )
}

/**
 * Everything booking a shipment needs, filled from what we already know and edited in place, then
 * one Book button (USA-1505). Carrier numbers are not asked for here: booking returns them.
 */
export function ShipmentBookingChecklist({
  po,
  shipment,
  bookShipmentMutation,
}: {
  po: PurchaseOrderDetail
  shipment: Shipment
  bookShipmentMutation: ReturnType<typeof useBookShipment>
}) {
  const { save, pending } = useShipmentSave(po, shipment)
  const missing = shipmentBookingMissing(shipment, po.ship_to_phone)
  const weightMissing = !(Number(shipment.total_weight_lbs ?? 0) > 0)
  const unitsWeight = (shipment.handling_units ?? []).reduce(
    (sum, unit) =>
      sum + unit.num_units * (unit.commodities ?? []).reduce((s, c) => s + Number(c.weight_lbs ?? 0), 0),
    0,
  )
  const suggestedWeight = weightMissing
    ? unitsWeight > 0
      ? unitsWeight
      : Number(po.estimated_weight_lbs ?? 0)
    : 0
  const shipTo = [po.ship_to_name, po.ship_to_company, po.ship_to_address1, [po.ship_to_city, po.ship_to_state].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ')
  const booking = bookShipmentMutation.isPending && bookShipmentMutation.variables === shipment.id
  const ltl = shipmentFreightMode(shipment, po) === 'ltl'
  // Ready to book with no failure: the booking is on its way; a failure shows its own Retry.
  const queued = shipment.status === 'ready_to_book' && !shipment.booking_error

  return (
    <section aria-label="Booking details" className="rounded-lg border">
      <div className="divide-y px-3">
        <Row label="Pickup" ok={pickupAddressMissing(shipment).length === 0 && Boolean(shipment.ship_from_phone)}>
          <PickupEditor po={po} shipment={shipment} />
        </Row>
        <Row label="Pickup date" ok={null}>
          <div className="flex flex-wrap items-center gap-x-1">
            <POInlineEditableField
              value={shipment.pickup_date ?? ''}
              displayValue={shipment.pickup_date ? formatDateOnly(shipment.pickup_date) : undefined}
              kind="date"
              placeholder="Any day"
              pending={pending}
              ariaLabel="Pickup date. Edit"
              buttonClassName={cn('-ml-2', !shipment.pickup_date && 'text-muted-foreground')}
              onConfirm={(raw) => save({ pickup_date: raw || null })}
            />
            <POInlineEditableField
              value={shipment.pickup_time_start ?? ''}
              placeholder="Opens"
              pending={pending}
              ariaLabel="Pickup opens at. Edit"
              buttonClassName={cn('min-w-16', !shipment.pickup_time_start && 'text-muted-foreground')}
              inputClassName="w-24"
              onConfirm={(raw) => save({ pickup_time_start: raw.trim() || null })}
            />
            <span className="text-muted-foreground" aria-hidden>
              –
            </span>
            <POInlineEditableField
              value={shipment.pickup_time_end ?? ''}
              placeholder="Closes"
              pending={pending}
              ariaLabel="Pickup closes at. Edit"
              buttonClassName={cn('min-w-16', !shipment.pickup_time_end && 'text-muted-foreground')}
              inputClassName="w-24"
              onConfirm={(raw) => save({ pickup_time_end: raw.trim() || null })}
            />
          </div>
        </Row>
        <Row label="Packages" ok={handlingUnitsMissing(shipment.handling_units ?? []).length === 0}>
          <UnitsEditor po={po} shipment={shipment} />
        </Row>
        <Row label="Total weight" ok={!weightMissing}>
          <div className="flex flex-wrap items-center gap-2">
            <POInlineEditableField
              value={shipment.total_weight_lbs ?? ''}
              displayValue={
                shipment.total_weight_lbs ? `${Number(shipment.total_weight_lbs).toLocaleString()} lb` : undefined
              }
              kind="number"
              placeholder="Add weight"
              pending={pending}
              ariaLabel="Total weight in pounds. Edit"
              buttonClassName={cn('-ml-2 tabular-nums', weightMissing && 'text-muted-foreground')}
              inputClassName="w-28"
              onConfirm={(raw) => save({ total_weight_lbs: raw.trim() || null })}
            />
            {suggestedWeight > 0 && (
              <Suggestion pending={pending} onClick={() => save({ total_weight_lbs: String(suggestedWeight) })}>
                Use {suggestedWeight.toLocaleString()} lb
              </Suggestion>
            )}
          </div>
        </Row>
        {ltl && (
          <Row label="Freight class" ok={null}>
            <POInlineEditableField
              value={shipment.freight_class ?? ''}
              placeholder="Carrier decides"
              pending={pending}
              ariaLabel="Freight class. Edit"
              buttonClassName={cn('-ml-2', !shipment.freight_class && 'text-muted-foreground')}
              inputClassName="w-24"
              onConfirm={(raw) => save({ freight_class: raw.trim() || null })}
            />
          </Row>
        )}
        <Row label="Deliver to" ok={Boolean(po.ship_to_phone)}>
          <p className="py-1.5 text-sm">
            <span className="block break-words">{shipTo || 'No ship-to on the PO'}</span>
            <span className={cn('block text-xs', po.ship_to_phone ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400')}>
              {po.ship_to_phone || 'Needs a delivery phone — add it under Ship to'}
            </span>
          </p>
        </Row>
        <Row label="Extras" ok={null}>
          <ExtrasEditor po={po} shipment={shipment} />
        </Row>
        <Row label="Notes" ok={null}>
          <POInlineEditableField
            value={shipment.shipping_notes ?? ''}
            kind="textarea"
            placeholder="Add a note for the carrier"
            pending={pending}
            allowWrap
            ariaLabel="Shipping notes. Edit"
            buttonClassName={cn('-ml-2 w-full justify-start', !shipment.shipping_notes && 'text-muted-foreground')}
            inputClassName="w-full"
            onConfirm={(raw) => save({ shipping_notes: raw.trim() || null })}
          />
        </Row>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-muted/30 px-3 py-2.5">
        {queued ? (
          <StatusPill tone="progress">Booking with the carrier</StatusPill>
        ) : missing.length > 0 ? (
          <StatusPill tone="warning">
            {missing.length} {missing.length === 1 ? 'detail' : 'details'} to add
          </StatusPill>
        ) : (
          <StatusPill tone="success">Ready to book</StatusPill>
        )}
        {!queued && (
          <LoadingButton
            size="sm"
            disabled={missing.length > 0}
            loading={booking}
            loadingText="Booking..."
            onClick={() =>
              bookShipmentMutation.mutate(shipment.id, {
                onSuccess: () => toast.success('Booking — the label or BOL goes to the supplier once the carrier confirms'),
                onError: (err) => toast.error(formatApiErrorMessage('Not booked', err)),
              })
            }
          >
            <Truck className="mr-1.5 size-3.5" />
            Book shipment
          </LoadingButton>
        )}
      </div>
    </section>
  )
}
